import type { ReactNode } from "react";
import { ChevronDown, ChevronRight, Clock } from "lucide-react";
import { theme } from "../theme";
import { FW_COLOR } from "../nav/frameworkMeta";
import { displayControlRef } from "../lib/controlRef";
import { StatusTag, type StatusTone } from "./Drawing";

// Shared visual language for a control's self-assessment status. The client
// framework pages, the consultant's view-as-client and the auditor's
// Controls & Findings list all render the same chip from these tables so the
// three roles see one control the same way.

export interface ControlStatusStyle {
  bg: string;
  color: string;
  border: string;
  label: string;
}

export const CONTROL_STATUS_EXPERT: Record<string, ControlStatusStyle> = {
  COMPLIANT:    { bg: "var(--c-successBg)", color: "var(--c-successText)", border: "var(--c-success)",      label: "Compliant" },
  GAP:          { bg: "var(--c-dangerBg)",  color: "var(--c-dangerText)",  border: "var(--c-danger)",       label: "Gap identified" },
  IN_PROGRESS:  { bg: "var(--c-warningBg)", color: "var(--c-warningText)", border: "var(--c-warning)",      label: "In progress" },
  NOT_STARTED:  { bg: "var(--c-border)",    color: "var(--c-faint)",       border: "var(--c-borderStrong)", label: "Not assessed" },
};

export const CONTROL_STATUS_NOVICE: Record<string, ControlStatusStyle> = {
  COMPLIANT:    { bg: "var(--c-successBg)", color: "var(--c-successText)", border: "var(--c-success)",      label: "We've done this" },
  GAP:          { bg: "var(--c-dangerBg)",  color: "var(--c-dangerText)",  border: "var(--c-danger)",       label: "Need to fix this" },
  IN_PROGRESS:  { bg: "var(--c-warningBg)", color: "var(--c-warningText)", border: "var(--c-warning)",      label: "Working on it" },
  NOT_STARTED:  { bg: "var(--c-border)",    color: "var(--c-faint)",       border: "var(--c-borderStrong)", label: "Not started yet" },
};

/** Framework swatch colour (nav/frameworkMeta FW_COLOR) — the one colour a standard carries everywhere. */
export function frameworkAccent(frameworkId: string): string {
  return (FW_COLOR as Record<string, string>)[frameworkId] ?? theme.color.accentText;
}

/**
 * "iso9001-4.1" → "4.1", "ncsc-caf-A1.a" → "A1.a": the reference as the client sees it, without
 * the internal prefix. One rule set for every page: lib/controlRef.ts. `frameworkId` is kept for
 * existing callers and no longer needed.
 */
export function displayRef(controlId: string, _frameworkId?: string): string {
  return displayControlRef(controlId);
}

/** Small bordered chip — the base for every status / count badge on a control card header. */
export function ControlChip({ bg, color, border, children, title }: {
  bg: string;
  color: string;
  border: string;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span
      title={title}
      style={{
        display: "inline-flex", alignItems: "center", gap: 3,
        padding: "2px 8px", borderRadius: 2, fontSize: 11, fontWeight: 700,
        background: bg, color, border: `1px solid ${border}`,
        flexShrink: 0, whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// The shape a status carries (components/Drawing.tsx StatusTag), so it reads without colour.
const STATUS_TONE: Record<string, StatusTone> = {
  COMPLIANT: "met",
  IN_PROGRESS: "partial",
  GAP: "gap",
  NOT_STARTED: "none",
};

export function ControlStatusBadge({ status, novice, prefix }: { status: string | null | undefined; novice?: boolean; prefix?: string }) {
  const table = novice ? CONTROL_STATUS_NOVICE : CONTROL_STATUS_EXPERT;
  const key = status && table[status] ? status : "NOT_STARTED";
  const cfg = table[key];
  return <StatusTag tone={STATUS_TONE[key] ?? "none"}>{prefix ? `${prefix}${cfg.label}` : cfg.label}</StatusTag>;
}

export function DocCountChip({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span style={{ fontSize: 10, color: theme.color.accentText, background: theme.color.accentBg, border: `1px solid ${theme.color.accent}`, borderRadius: 2, padding: "1px 6px", flexShrink: 0, whiteSpace: "nowrap" }}>
      {count} doc{count !== 1 ? "s" : ""}
    </span>
  );
}

/** SoA entry awaiting consultant approval (approvalStatus === "DRAFT"). */
export function PendingApprovalChip() {
  return (
    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 2, background: "var(--c-warningBg)", color: "var(--c-warningText)", border: "1px solid var(--c-warning)", flexShrink: 0, whiteSpace: "nowrap" }}>
      <Clock size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />Pending approval
    </span>
  );
}

/** Collapsible domain / section header above a run of control cards, tinted with the framework colour. */
export function ControlSectionHeader({ accent, expanded, title, count, compliant, gap, onToggle, disabled }: {
  accent: string;
  expanded: boolean;
  title: string;
  count: number;
  compliant: number;
  gap: number;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={() => !disabled && onToggle()}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "10px 14px", borderRadius: expanded ? "3px 3px 0 0" : 3,
        background: accent + "18",
        border: `1px solid ${accent}44`,
        borderBottom: expanded ? `1px solid ${accent}22` : `1px solid ${accent}44`,
        cursor: disabled ? "default" : "pointer", textAlign: "left",
      }}
    >
      <span aria-hidden="true" style={{ display: "flex", color: accent, flexShrink: 0 }}>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, flex: 1 }}>{title}</span>
      <span style={{ fontSize: 11, color: theme.color.faint, flexShrink: 0 }}>
        {count} control{count !== 1 ? "s" : ""}
      </span>
      {compliant > 0 && <StatusTag tone="met">{compliant} compliant</StatusTag>}
      {gap > 0 && <StatusTag tone="gap">{gap} gap{gap !== 1 ? "s" : ""}</StatusTag>}
    </button>
  );
}

/** Body wrapper under an expanded ControlSectionHeader. */
export function ControlSectionBody({ accent, children }: { accent: string; children: ReactNode }) {
  return (
    <div style={{ border: `1px solid ${accent}44`, borderTop: "none", borderRadius: "0 0 3px 3px", padding: "10px 10px", display: "flex", flexDirection: "column", gap: 8, background: theme.color.bg, marginBottom: 4 }}>
      {children}
    </div>
  );
}
